import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_AVATAR_URL } from '../../constants/assets';

export interface BriefCardData {
    id: string | number;
    title: string;
    publisher?: string;
    client?: string;
    avatar?: string;
    userId?: string;
    budgetText?: string;
    deadlineText?: string;
    durationText?: string;
    viewCount?: number;
    communicationCount?: number;
    trialCount?: number;
    status?: 'draft' | 'active' | 'closed' | 'archived';
    briefType?: 'standard' | 'lightweight';
}

interface BriefCardProps {
    brief: BriefCardData;
    onClick: () => void;
    showPublisher?: boolean;
    variant?: 'white' | 'muted';
    className?: string;
    dragHandle?: React.ReactNode;
}

const DEFAULT_AVATAR = DEFAULT_AVATAR_URL;

const formatCount = (value?: number) => new Intl.NumberFormat('zh-CN').format(value ?? 0);

const normalizeBudgetText = (budgetText?: string) => {
    const value = budgetText?.trim();
    if (!value) return '可谈';
    return value.replace(/^[¥￥]\s*/, '');
};

const BriefCardStat = ({ label, value }: { label: string; value: number }) => (
    <div className="flex items-baseline gap-1 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
        <span>{label}</span>
        <span className="font-semibold text-gray-900">{formatCount(value)}</span>
    </div>
);

const BriefCard: React.FC<BriefCardProps> = ({
    brief,
    onClick,
    showPublisher = true,
    variant = 'muted',
    className = '',
    dragHandle
}) => {
    const navigate = useNavigate();
    const publisherName = brief.publisher || brief.client || '匿名需求方';
    const isArchived = brief.status === 'archived';
    const cardToneClass = isArchived
        ? 'relative overflow-hidden border-gray-200 bg-gray-100/80 opacity-75 hover:border-gray-200 hover:bg-gray-100/80'
        : variant === 'white'
        ? 'border-[#EEF2F6] bg-white hover:border-[#E5EAF1] hover:bg-white shadow-[0_8px_20px_rgba(15,23,42,0.04)]'
        : 'border-[#EEF1F5] bg-[#FBFBFC] hover:border-[#E6EBF2] hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.05)]';
    const titleClass = isArchived
        ? 'text-gray-500'
        : 'text-gray-900 group-hover:text-blue-600';

    return (
        <div
            onClick={onClick}
            className={`group flex flex-col lg:flex-row lg:items-center justify-between gap-5 p-6 md:p-7 rounded-[2rem] border transition-all duration-300 cursor-pointer shrink-0 ${cardToneClass} ${className}`}
        >
            {isArchived && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center text-5xl font-black tracking-[0.35em] text-gray-300/60"
                    style={{ transform: 'rotate(-10deg)' }}
                >
                    已下架
                </div>
            )}
            <div className="relative z-10 flex-1 min-w-0">
                {showPublisher && (
                    <div className="flex items-center gap-3 mb-2">
                        <span
                            className={`flex items-center gap-3 ${brief.userId ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                            onClick={(e) => { if (brief.userId) { e.stopPropagation(); navigate(`/profile/${brief.userId}`); } }}
                        >
                        <img
                            src={brief.avatar || DEFAULT_AVATAR}
                            alt={publisherName}
                            className="w-7 h-7 rounded-full object-cover bg-gray-100 shrink-0"
                            onError={(event) => {
                                event.currentTarget.src = DEFAULT_AVATAR;
                            }}
                        />
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
                            {publisherName}
                        </span>
                        </span>
                    </div>
                )}

                <div className="mb-3 flex items-start gap-2">
                    {dragHandle}
                    <h3 className={`text-lg md:text-xl font-medium transition-colors line-clamp-2 ${titleClass}`}>
                        {brief.title}
                    </h3>
                    {brief.status === 'draft' && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
                            草稿
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                        <span>截止: {brief.deadlineText || '可谈'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                        <span>工期: {brief.durationText || '可谈'}</span>
                    </div>
                </div>
            </div>

            <div className="relative z-10 flex flex-col gap-3 lg:items-end lg:text-right shrink-0 lg:min-w-[290px]">
                <div className="flex items-center justify-end gap-4 w-full">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-base font-semibold text-blue-700">
                        <span className="text-base font-semibold leading-none text-blue-500">¥</span>
                        {normalizeBudgetText(brief.budgetText)}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 lg:justify-end">
                    <BriefCardStat label="浏览" value={brief.viewCount ?? 0} />
                    <BriefCardStat label="沟通" value={brief.communicationCount ?? 0} />
                    <BriefCardStat label="试稿" value={brief.trialCount ?? 0} />
                </div>
            </div>
        </div>
    );
};

export default BriefCard;
